import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Edit2, Trash2 } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { Table, type Column } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { PageLoader } from '../../components/ui/PageLoader/PageLoader';
import { useCustomerDetail } from './hooks/useCustomerDetail';
import { useCustomerBookings } from './hooks/useCustomerBookings';
import { useDeleteCustomer } from './hooks/useCustomerMutations';
import { CustomerBankHistoryCard } from './components/CustomerBankHistoryCard';
import { useAuthStore } from '../../store/authStore';
import type { AccountStatus, BookingStatus, CustomerBookingHistoryItem } from '../../types/api';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import styles from './CustomerDetailPage.module.css';

const ACCOUNT_STATUS_MAP: Record<AccountStatus, BadgeVariant> = {
  active: 'success',
  inactive: 'warning',
  suspended: 'danger',
};

const BOOKING_STATUS_MAP: Record<BookingStatus, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'danger',
  cancellation_requested: 'warning',
};

const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  cancellation_requested: 'Cancellation requested',
};

type Tab = 'bookings' | 'receipts' | 'notes' | 'activity';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

export function CustomerDetailPage(): JSX.Element {
  const { id: uuid } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const tab: Tab = (['bookings', 'receipts', 'notes', 'activity'].includes(
    params.get('tab') ?? '',
  )
    ? (params.get('tab') as Tab)
    : 'bookings');

  const [bookingsPage, setBookingsPage] = useState(1);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const detail = useCustomerDetail(uuid ?? '');
  const bookingsQuery = useCustomerBookings(uuid ?? '', bookingsPage);
  const deleteMutation = useDeleteCustomer();

  function selectTab(next: Tab) {
    const next2 = new URLSearchParams(params);
    next2.set('tab', next);
    setParams(next2, { replace: false });
  }

  const bookingColumns: Column<CustomerBookingHistoryItem>[] = useMemo(
    () => [
      {
        key: 'reference',
        header: 'Reference',
        render: (_v, row) => (
          <span
            style={{ color: 'var(--color-primary)', cursor: 'pointer' }}
            onClick={() => navigate(`/collections/bookings/${encodeURIComponent(row.uuid)}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter')
                navigate(`/collections/bookings/${encodeURIComponent(row.uuid)}`);
            }}
          >
            {row.reference}
          </span>
        ),
      },
      {
        key: 'car_make_model',
        header: 'Vehicle',
        render: (_v, row) => (
          <div>
            <span>{row.car_make_model}</span>
            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
              {row.car_registration}
            </p>
          </div>
        ),
      },
      {
        key: 'date',
        header: 'Date',
        render: (_v, row) => formatDate(row.date),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => (
          <StatusBadge
            status={row.status ?? null}
            statusMap={BOOKING_STATUS_MAP}
            formatLabel={(s) => BOOKING_STATUS_LABELS[s as BookingStatus] ?? s}
          />
        ),
      },
      {
        key: 'price',
        header: 'Price',
        align: 'right',
        render: (_v, row) => formatMoney(row.price),
      },
    ],
    [navigate],
  );

  if (detail.isLoading) {
    return <PageLoader message="Loading customer…" />;
  }

  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Customer" subtitle="Could not load this customer." />
        <Card>
          <p>We were unable to load the customer record. The record may not exist.</p>
          <div className={styles.errorActions}>
            <Button variant="secondary" onClick={() => navigate('/customers')}>
              Back to Customers
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const customer = detail.data;
  const bookings = bookingsQuery.data?.bookings ?? [];
  const bookingPag = bookingsQuery.data?.pagination;

  return (
    <div className={styles.page}>
      <PageHeader
        title={customer.full_name}
        subtitle={customer.customer_number ?? undefined}
        actions={
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <Button
              variant="secondary"
              icon={Edit2}
              onClick={() =>
                navigate(`/customers/${encodeURIComponent(customer.uuid)}/edit`)
              }
            >
              Edit
            </Button>
            {isAdmin && !customer.deleted_at ? (
              <Button
                variant="danger"
                icon={Trash2}
                onClick={() => setDeleteOpen(true)}
              >
                Delete
              </Button>
            ) : null}
          </div>
        }
      />

      {customer.deleted_at ? (
        <div style={{ padding: '0.75rem 1rem', background: 'var(--color-danger-bg, var(--color-bg-danger))', borderRadius: 6, fontSize: '0.875rem' }}>
          <Badge variant="danger">Deleted</Badge>{' '}
          This customer record was deleted on {formatDate(customer.deleted_at)}.
        </div>
      ) : null}

      {/* Quick-created at the kiosk and still missing billing details. WB
          ticket completion stays blocked until these are filled in. */}
      {customer.is_complete === false ? (
        <div
          role="alert"
          data-testid="customer-incomplete-banner"
          style={{
            padding: '0.75rem 1rem',
            background: 'var(--color-status-warning-bg, #fef3c7)',
            color: 'var(--color-status-warning-text, #92400e)',
            border: '1px solid var(--color-status-warning-text, #92400e)',
            borderRadius: 6,
            fontSize: '0.875rem',
          }}
        >
          <Badge variant="warning">Incomplete</Badge>{' '}
          This customer record is incomplete. Add{' '}
          {[
            (customer.phone ?? '').trim() === '' && (customer.email ?? '') === null
              ? 'phone or email'
              : null,
            (customer.address ?? '').trim() === '' ? 'address' : null,
            (customer.postcode ?? '').trim() === '' ? 'postcode' : null,
          ]
            .filter(Boolean)
            .join(', ') || 'the missing contact details'}{' '}
          to enable weighbridge ticket completion.
        </div>
      ) : null}

      <Card title="Profile">
        <div className={styles.profileGrid}>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Name</span>
            <span className={styles.profileValue}>{customer.full_name}</span>
          </div>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Customer type</span>
            <span className={styles.profileValue}>
              {customer.customer_type === 'commercial' ? 'Commercial' : 'Residential'}
            </span>
          </div>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Phone</span>
            <span className={styles.profileValue}>
              <a href={`tel:${customer.phone}`}>{customer.phone}</a>
            </span>
          </div>
          {customer.phone2 ? (
            <div className={styles.profileItem}>
              <span className={styles.profileLabel}>Phone (alt)</span>
              <span className={styles.profileValue}>
                <a href={`tel:${customer.phone2}`}>{customer.phone2}</a>
              </span>
            </div>
          ) : null}
          {customer.email ? (
            <div className={styles.profileItem}>
              <span className={styles.profileLabel}>Email</span>
              <span className={styles.profileValue}>
                <a href={`mailto:${customer.email}`}>{customer.email}</a>
              </span>
            </div>
          ) : null}
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Address</span>
            <span className={styles.profileValue}>
              {customer.address}, {customer.postcode}
            </span>
          </div>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Account status</span>
            <span className={styles.profileValue}>
              <StatusBadge
                status={customer.account_status ?? null}
                statusMap={ACCOUNT_STATUS_MAP}
              />
            </span>
          </div>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Total jobs</span>
            <span className={styles.profileValue}>{customer.total_jobs}</span>
          </div>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Total spent</span>
            <span className={styles.profileValue}>{formatMoney(customer.total_spent)}</span>
          </div>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Last job</span>
            <span className={styles.profileValue}>{formatDate(customer.last_job_date)}</span>
          </div>
          <div className={styles.profileItem}>
            <span className={styles.profileLabel}>Customer number</span>
            <span className={styles.profileValue}>{customer.customer_number ?? '—'}</span>
          </div>
          {isAdmin && (customer.account_name || customer.account_number || customer.sort_code) ? (
            <>
              {customer.account_name ? (
                <div className={styles.profileItem}>
                  <span className={styles.profileLabel}>Account name</span>
                  <span className={styles.profileValue}>{customer.account_name}</span>
                </div>
              ) : null}
              {customer.account_number ? (
                <div className={styles.profileItem}>
                  <span className={styles.profileLabel}>Account number</span>
                  <span className={styles.profileValue}>{customer.account_number}</span>
                </div>
              ) : null}
              {customer.sort_code ? (
                <div className={styles.profileItem}>
                  <span className={styles.profileLabel}>Sort code</span>
                  <span className={styles.profileValue}>{customer.sort_code}</span>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </Card>

      <CustomerBankHistoryCard uuid={uuid ?? ''} isAdmin={isAdmin} />

      <div role="tablist" className={styles.tabStrip} aria-label="Customer tabs">
        {(['bookings', 'receipts', 'notes', 'activity'] as Tab[]).map((t) => (
          <button
            key={t}
            role="tab"
            type="button"
            aria-selected={tab === t}
            aria-controls={`tab-panel-${t}`}
            tabIndex={tab === t ? 0 : -1}
            className={tab === t ? styles.tabActive : styles.tab}
            onClick={() => selectTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'bookings' ? (
        <div id="tab-panel-bookings" role="tabpanel">
        <Card>
          <Table
            columns={bookingColumns}
            data={bookings}
            keyField={(row) => row.uuid}
            loading={bookingsQuery.isLoading}
            emptyState={
              <div className={styles.empty}>
                <p>No booking history for this customer.</p>
              </div>
            }
            ariaLabel="Customer booking history"
          />
          {(bookingPag?.totalPages ?? 0) > 1 ? (
            <div className={styles.paginationWrap}>
              <Pagination
                pagination={bookingPag ?? { page: 1, limit: 50, total: 0, totalPages: 1 }}
                onPageChange={(next) => setBookingsPage(next)}
              />
            </div>
          ) : null}
        </Card>
        </div>
      ) : null}

      {tab === 'receipts' ? (
        <div id="tab-panel-receipts" role="tabpanel">
          <Card>
            <p className={styles.comingSoon}>
              Receipt history by customer — coming soon
            </p>
          </Card>
        </div>
      ) : null}

      {tab === 'notes' ? (
        <div id="tab-panel-notes" role="tabpanel">
          <Card>
            <p className={styles.comingSoon}>Customer notes — coming soon</p>
          </Card>
        </div>
      ) : null}

      {tab === 'activity' ? (
        <div id="tab-panel-activity" role="tabpanel">
          <Card>
            <p className={styles.comingSoon}>Activity feed — coming soon</p>
          </Card>
        </div>
      ) : null}

      <ConfirmModal
        open={deleteOpen}
        title="Delete customer?"
        message={`Delete ${customer.full_name}? They can be restored later.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          deleteMutation.mutate(customer.uuid, {
            onSuccess: () => {
              setDeleteOpen(false);
              navigate('/customers');
            },
          });
        }}
      />
    </div>
  );
}
